import type { ISchedulerTask, TaskId, TaskKind } from '../../interfaces/IScheduler.js';
import { ladderOf, type ILadderActivity, type LadderRung } from './ladder.js';

// ---------------------------------------------------------------------------
// What may still be edited once the field has been OBSERVED working on a task
// (docs/specs/2026-09-20-observed-work-is-frozen-amendment.md).
//
// A date means a different thing before and after work is seen: before it is an
// intention, after it is a record. Trusstor gates its own workplan inputs on the
// activity group's status, but only in its UI — its `updateActivity` has no such
// guard, so an edit D-ORCA sends is accepted whatever the group is doing, and
// moving a started activity's start date makes Trusstor rewrite
// `originalStartDate` and reset the group to `planned`. D-ORCA therefore has to
// enforce the rule; nothing upstream will.
//
// The rule is a function of the LADDER RUNG, so the rail chip, the drawer, the
// drag gestures and the cascade all read one source. This module is the rule;
// every surface that blocks something asks it rather than re-deriving it.
// ---------------------------------------------------------------------------

/**
 * How much of a task's plan is a record rather than an intention.
 *
 *  · `none`  — nothing observed; the plan is a plan.
 *  · `scope` — WHO and WHERE are fixed, WHEN is still open.
 *  · `start` — `scope`, and the start date is an observation.
 *  · `all`   — the record is closed.
 */
export type FreezeLevel = 'none' | 'scope' | 'start' | 'all';

/** The plan fields D-ORCA writes through to a Trusstor activity. */
export type FreezableField =
  | 'name'
  | 'startDate'
  | 'endDate'
  | 'areas'
  | 'profession'
  | 'assignee'
  | 'dangerous';

const LEVEL_BY_RUNG: Record<LadderRung, FreezeLevel> = {
  // Nothing has been seen on the floor yet — including a committed task, whose
  // whole point is that it is still re-plannable until the crew arrives.
  planned: 'none',
  blocked: 'none',
  committed: 'none',
  // Trusstor's row: the group is live (its planned start has passed) even though
  // no work has been observed, and it locks the crew and the areas there.
  delayed: 'scope',
  inProgress: 'start',
  overdue: 'start',
  // `areasDone` is Trusstor's `complete`. Done is still human (invariant 6), but
  // every area is finished, so there is nothing left to plan either way.
  areasDone: 'all',
  done: 'all',
};

/** WHO and WHERE: fixed the moment Trusstor's group stops being `planned`. */
const SCOPE_FIELDS: ReadonlySet<FreezableField> = new Set<FreezableField>([
  'areas',
  'profession',
  'assignee',
  'dangerous',
]);

/**
 * An administrative activity climbs the same rungs, but from its own reported
 * progress and dates — nothing upstream rewrites it and nothing on the floor
 * observed it, so only closing the record freezes it. Re-planning its dates is
 * how a manager answers its "delayed".
 */
export function freezeOf(rung: LadderRung, kind: TaskKind = 'field'): FreezeLevel {
  if (kind === 'administrative' && rung !== 'done') return 'none';
  return LEVEL_BY_RUNG[rung];
}

/** The rung of a task, and with it its freeze level, in one step. */
export function freezeOfTask(
  task: Pick<ISchedulerTask, 'status' | 'committed' | 'kind'>,
  activity?: ILadderActivity,
): FreezeLevel {
  return freezeOf(ladderOf(task, activity).rung, task.kind);
}

/**
 * May this field still be edited? The name and the label stay open until the
 * record closes, matching Trusstor — a task can always be renamed while it runs.
 */
export function isFrozen(level: FreezeLevel, field: FreezableField): boolean {
  switch (level) {
    case 'none':
      return false;
    case 'scope':
      return SCOPE_FIELDS.has(field);
    case 'start':
      return SCOPE_FIELDS.has(field) || field === 'startDate';
    case 'all':
      return true;
  }
}

/**
 * What the cascade needs to know, which is only ever about dates:
 * `start` pins the start (the end may still move), `all` takes the task out of
 * the cascade entirely. `null` means the cascade may treat it as a plain plan
 * row — the `scope` level fixes the crew and the areas, not the calendar.
 */
export type CascadeFreeze = 'start' | 'all';

export function cascadeFreezeOf(level: FreezeLevel): CascadeFreeze | null {
  if (level === 'all') return 'all';
  if (level === 'start') return 'start';
  return null;
}

/**
 * The map `cascade` takes, built from the plan rows and the activity feed. Only
 * frozen tasks are keyed, so an ordinary schedule yields an empty map and the
 * cascade behaves exactly as it did before.
 */
export function frozenTasks(
  tasks: readonly Pick<ISchedulerTask, 'id' | 'status' | 'kind'>[],
  byTask: ReadonlyMap<string, ILadderActivity>,
): Map<TaskId, CascadeFreeze> {
  const frozen = new Map<TaskId, CascadeFreeze>();
  for (const task of tasks) {
    // The feed IS the committed set — it lists committed activities and nothing
    // else (invariant 2) — so presence in it answers `committed` for a plan row,
    // which does not carry the flag. A task absent from it can still freeze by
    // being `done`, which is why the plan list drives the loop.
    const activity = byTask.get(task.id);
    const level = cascadeFreezeOf(freezeOfTask({ ...task, committed: activity !== undefined }, activity));
    if (level) frozen.set(task.id, level);
  }
  return frozen;
}
